import * as React from 'react';
import * as echarts from 'echarts/core';
// Import bar charts, all suffixed with Chart
import { BarChart, LineChart } from 'echarts/charts';

// Import the title, tooltip, rectangular coordinate system, dataset and transform components
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LegendComponent,
} from 'echarts/components';
// Features like Universal Transition and Label Layout
import { LabelLayout, UniversalTransition } from 'echarts/features';

// Import the Canvas renderer
// Note that including the CanvasRenderer or SVGRenderer is a required step
import { SVGRenderer } from 'echarts/renderers';

import { format, parseISO } from 'date-fns';

import type {
    DatasetComponentOption,
    GridComponentOption,
    BarSeriesOption,
    LineSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
} from 'echarts';

// Create an Option type with only the required components and charts via ComposeOption
type ECOption = echarts.ComposeOption<
    | BarSeriesOption
    | LineSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | GridComponentOption
    | DatasetComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    BarChart,
    LineChart,
    LegendComponent,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface TrendData {
    period: string;
    period_start: string;
    new_visitors?: number;
    returning_visitors?: number;
    total_visitors?: number;
}

interface VisitorTrendChartProps {
    data?: TrendData[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
}

const VisitorTrendChart = (props: VisitorTrendChartProps) => {
    const { data, period } = props;
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!data || data.length === 0) {
            // Clear chart if no data
            if (chartInstance.current && chartRef.current) {
                chartInstance.current.setOption({
                    series: [
                        { data: [] },
                        { data: [] }
                    ]
                });
            }
            return;
        }
        
        // Initialize chart
        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            // Format data for chart
            const categories: string[] = [];
            const newSeries: number[] = [];
            const returningSeries: number[] = [];
            const totalSeries: number[] = [];
            const totalByIndex: Record<number, number> = {};

            data.forEach(item => {
                let date: Date;
                try {
                    if (item.period_start) {
                        date = parseISO(item.period_start);
                        if (isNaN(date.getTime())) {
                            date = new Date(item.period_start);
                        }
                    } else {
                        date = new Date();
                    }
                } catch (e) {
                    console.warn('Error parsing date:', item.period_start, e);
                    date = new Date(item.period_start || new Date());
                }

                let label = '';
                if (period === 'daily') {
                    label = format(date, 'MMM dd');
                } else if (period === 'weekly') {
                    label = format(date, 'MMM dd');
                } else if (period === 'monthly') {
                    label = format(date, 'MMM yyyy');
                } else {
                    label = format(date, 'QQQ yyyy');
                }

                categories.push(label);
                newSeries.push(item.new_visitors || 0);
                returningSeries.push(item.returning_visitors || 0);
                const total =
                    item.total_visitors ||
                    (item.new_visitors || 0) + (item.returning_visitors || 0);
                totalSeries.push(total);
                totalByIndex[categories.length - 1] = total;
            });

            // Configure the chart
            const option: ECOption = {
                legend: {
                    data: ['New Visitors', 'Returning Visitors', 'Total Visitors'],
                    top: 0,
                },
                xAxis: {
                    type: 'category',
                    data: categories,
                    axisLabel: {
                        interval: 0,
                        rotate: period === 'monthly' || period === 'quarterly' ? 0 : 45,
                    },
                },
                yAxis: {
                    type: 'value',
                    axisLabel: {
                        formatter: (value: number) => `${value}`,
                    },
                    splitLine: {
                        show: true,
                        lineStyle: {
                            type: [3, 4],
                            color: '#aaa',
                        },
                    },
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow'
                    },
                    formatter: (params: any) => {
                        const lines = params.map((p: any) => `${p.marker} ${p.seriesName}: ${p.data}`);
                        const label = params[0]?.axisValue || '';
                        // compute total from stacked bars to show even if line hidden
                        const idx = params[0]?.dataIndex ?? 0;
                        const totalVal = totalByIndex[idx] ?? 0;
                        return [`${label}`, ...lines, `Total Visitors: ${totalVal}`].join('<br/>');
                    },
                },
                grid: {
                    left: '0%',
                    right: '1%',
                    bottom: '10%',
                    top: '12%',
                    containLabel: true,
                },
                series: [
                    {
                        name: 'New Visitors',
                        type: 'bar',
                        stack: 'visitors',
                        barMaxWidth: 30,
                        itemStyle: { color: '#1890ff' },
                        data: newSeries,
                    },
                    {
                        name: 'Returning Visitors',
                        type: 'bar',
                        stack: 'visitors',
                        barMaxWidth: 30,
                        itemStyle: { color: '#52c41a' },
                        data: returningSeries,
                    },
                    {
                        name: 'Total Visitors',
                        type: 'line',
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: 6,
                        lineStyle: { color: '#fa8c16', width: 2 },
                        itemStyle: { color: '#fa8c16' },
                        data: totalSeries,
                    },
                ],
            };

            // Apply the config
            chartInstance.current.setOption(option);
        }

        // Handle resize
        const handleResize = () => {
            chartInstance.current?.resize();
        };

        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            if (chartInstance.current) {
                chartInstance.current.dispose();
                chartInstance.current = null;
            }
        };
    }, [data, period]);

    return <div ref={chartRef} style={{ width: '100%', height: 300 }} />;
};

export default VisitorTrendChart;

